import { post } from '@/api/client'
import type { CustomOAuthProvider } from '@/types'

export type OAuthIntent = 'login' | 'bind'

/** 前端回调路由（与 src/router/index.tsx 保持一致） */
export const oauthCallbackPath = (provider: string) => `/oauth2/callback/${provider}`

/** 回调页绝对地址 */
export const oauthCallbackUrl = (provider: string) =>
  `${window.location.origin}${oauthCallbackPath(provider)}`

export interface OAuthStartOptions {
  provider: string
  intent: OAuthIntent
  /** 回调页地址，例如 `${origin}/oauth2/github` */
  redirectUri: string
  /** 仅登录: 邀请码 */
  aff?: string
  /** 仅绑定: 后端签发的一次性安全凭据 */
  securityProof?: string
  /** 自定义 provider 的授权端点（仅自定义 provider 需要） */
  endpoint?: string
  clientId?: string
  scopes?: string
}

interface OAuthStateResult {
  flow_token?: string
  expires_at?: number
  /** Telegram 由后端生成授权地址 */
  authorization_url?: string
  state?: string
}

/**
 * 申请 OAuth flow_token。
 * new-api v1.0.0 起是 POST /api/oauth/state，且绑定需要 X-Security-Proof。
 */
export async function requestOAuthState(options: OAuthStartOptions): Promise<OAuthStateResult> {
  return post<OAuthStateResult>(
    '/api/oauth/state',
    {
      provider: options.provider,
      intent: options.intent,
      ...(options.aff ? { aff: options.aff } : {}),
    },
    {
      skipAuthRefresh: options.intent === 'login',
      headers: options.securityProof ? { 'X-Security-Proof': options.securityProof } : undefined,
    }
  )
}

function buildAuthorizeUrl(options: OAuthStartOptions, state: string): string {
  const { provider, redirectUri, endpoint, clientId = '', scopes } = options
  switch (provider) {
    case 'github':
      return `https://github.com/login/oauth/authorize?client_id=${clientId}&state=${state}&scope=user:email`
    case 'discord':
      return `https://discord.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&response_type=code&scope=identify+openid&state=${state}`
    case 'linuxdo':
      return `https://connect.linux.do/oauth2/authorize?response_type=code&client_id=${clientId}&state=${state}`
    case 'oidc': {
      const url = new URL(endpoint || '')
      url.searchParams.set('client_id', clientId)
      url.searchParams.set('redirect_uri', redirectUri)
      url.searchParams.set('response_type', 'code')
      url.searchParams.set('scope', scopes || 'openid profile email')
      url.searchParams.set('state', state)
      return url.toString()
    }
    default: {
      // 自定义 OAuth provider：端点由后台配置下发
      const url = new URL(endpoint || '')
      url.searchParams.set('client_id', clientId)
      url.searchParams.set('redirect_uri', redirectUri)
      url.searchParams.set('response_type', 'code')
      url.searchParams.set('scope', scopes || 'openid profile email')
      url.searchParams.set('state', state)
      return url.toString()
    }
  }
}

/**
 * 发起 OAuth 登录/绑定：拿 flow_token → 跳转授权页。
 * Telegram 由后端返回完整授权地址。
 */
export async function startOAuth(options: OAuthStartOptions): Promise<void> {
  const data = await requestOAuthState(options)
  const state = data.flow_token || data.state || ''
  if (!state) throw new Error('无法获取 OAuth state')
  const target = data.authorization_url || buildAuthorizeUrl(options, state)
  if (!target) throw new Error('OAuth 配置错误：缺少授权端点')
  window.location.href = target
}

/** 自定义 OAuth provider 绑定 */
export async function startCustomOAuth(
  provider: CustomOAuthProvider,
  intent: OAuthIntent,
  securityProof?: string
): Promise<void> {
  const endpoint = provider.authorization_endpoint
  if (!endpoint?.startsWith('http://') && !endpoint?.startsWith('https://')) {
    throw new Error('OAuth 配置错误：授权端点必须是完整的 URL')
  }
  await startOAuth({
    provider: provider.slug,
    intent,
    redirectUri: `${window.location.origin}/oauth2/${provider.slug}`,
    securityProof,
    endpoint,
    clientId: provider.client_id || '',
    scopes: provider.scopes || 'openid profile email',
  })
}
